import { useState, useRef, useEffect, useCallback } from 'react';
import { BASE_URL } from '../apiConfig';
import { useAuth } from "../context/AuthProvider";
import Cookies from 'js-cookie';
import { createPortal } from 'react-dom';
import { Plus, Minus, ShoppingCart, Trash2, X } from "lucide-react";
import VariantSelector from "../components/VariantSelector";
import { toast } from 'sonner';
import axios from "axios";
import Select from 'react-select';


export default function OrderModal({ isOpen, onClose, product }) {
  const [cities, setCities] = useState([]);
  const { token } = useAuth();

  const updateOrderOption = useCallback((clientId, optionField, value) => {
    setClients(prevClients =>
      prevClients.map(client =>
        client.id === clientId
          ? { ...client, [optionField]: value }
          : client
      )
    );
  }, []);

  const fetchCities = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/getallcities`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        if (!response.ok) {
          throw new Error(`Failed to fetch cities: ${response.statusText}`)
        }
        const result = await response.json()
  
        if (result?.data) {
          setCities(result.data)
        } else {
          setCities([])
          toast.error("Received unexpected data format from cities API.")
        }
      } catch (err) {
        toast.error(err.message || "Error loading cities.")
      }
    }

  const modalRef = useRef(null);
  const saller_id = JSON.parse(Cookies.get("user"))
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState(() => {
    if (product) {
      return [{
        id: "1",
        nom: "",
        phone: "",
        address: "",
        city: "",
        delivery_price: 0,
        change_price: 0,
        sellingPrice: "",
        comment: "",
        quantity: 1,
        change: false,
        unlockable: true,
        confirme: false,
        variantSections: [{
          id: `variant-${Date.now()}`,
          selectedVariants: {},
        }],
      }];
    }
    return [];
  });

  useEffect(() => {
    if (product && clients.length === 0) {
      setClients([{
        id: "1",
        nom: "",
        phone: "",
        address: "",
        city: "",
        delivery_price: 0,
        change_price: 0,
        sellingPrice: "",
        comment: "",
        quantity: 1,
        change: false,
        unlockable: true,
        confirme: false,
        variantSections: [{
          id: `variant-${Date.now()}`,
          selectedVariants: {},
        }],
      }]);
    }
    fetchCities()
  }, [product, clients.length]);

  if (!isOpen) return null;

  const cityOptions = cities.map(city => ({
    value: city.id,
    label: city.name,
    deliver: city.deliver,
    return: city.return,
    refuse: city.refuse,
    change: city.change,
  }));

  const addClient = () => {
    const newClient = {
      id: Date.now().toString(),
      nom: "",
      phone: "",
      address: "",
      city: "",
      delivery_price: 0,
      change_price: 0,
      sellingPrice: "",
      comment: "",
      quantity: 1,
      change: false,
      unlockable: true,
      confirme: false,
      variantSections: [{
        id: `variant-${Date.now()}`,
        selectedVariants: {},
      }],
    };
    setClients([...clients, newClient]);
  };

  const removeClient = (id) => {
    if (clients.length > 1) {
      setClients(clients.filter(client => client.id !== id));
    } else {
      toast.warn("Vous devez avoir au moins un client.");
    }
  };

  const updateClientInfo = (id, field, value) => {
    setClients(prevClients =>
      prevClients.map(client => {
        if (client.id !== id) return client;

        if (field === null && typeof value === 'object') {
          return { ...client, ...value };
        }

        return { ...client, [field]: value };
      })
    );
  };

  const updateQuantity = (clientId, newQuantity) => {
    setClients(prevClients =>
      prevClients.map(client => {
        if (client.id !== clientId) return client;

        // Calculate difference in quantity
        const currentQty = client.quantity;
        const diff = newQuantity - currentQty;

        if (diff > 0) {
          // Add new variant sections
          const newSections = Array(diff).fill().map(() => ({
            id: `variant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            selectedVariants: {},
          }));

          return {
            ...client,
            quantity: newQuantity,
            variantSections: [...client.variantSections, ...newSections]
          };
        } else if (diff < 0) {
          // Remove variant sections
          return {
            ...client,
            quantity: newQuantity,
            variantSections: client.variantSections.slice(0, newQuantity)
          };
        }

        return client;
      })
    );
  };

  const getClientProfit = (client) => {
    const sellingPrice = parseFloat(client.sellingPrice || 0);
    const originalPrice = parseFloat(getProductOriginalPriceDisplay(product).toFixed(2));
    const deliveryCost = client.change
      ? parseFloat(client.change_price || 0)
      : parseFloat(client.delivery_price || 0);

    return (sellingPrice - originalPrice) * client.quantity - deliveryCost;
  };

  const updateVariantSelection = (clientId, variantIndex, variants) => {
    setClients(prevClients =>
      prevClients.map(client =>
        client.id === clientId
          ? {
              ...client,
              variantSections: client.variantSections.map((section, idx) =>
                idx === variantIndex
                  ? { ...section, selectedVariants: variants }
                  : section
              ),
            }
          : client
      )
    );
  };

  const getProductOriginalPriceDisplay = (prod) => {
    const sallerPrice = parseFloat(prod.price?.saller_price || 0);
    const today = new Date();
    let finalPrice = sallerPrice;

    if (prod.discount && prod.discount.status === "true") {
      const start = new Date(prod.discount.date_start);
      const end = new Date(prod.discount.date_end);
      if (today >= start && today <= end) {
        const discount = parseFloat(prod.discount.value || 0);
        finalPrice = sallerPrice - (sallerPrice * discount) / 100;
      }
    }
    
    return finalPrice;
  };

  const getClientTotal = (client) => {
    const enteredPrice = parseFloat(client.sellingPrice || 0);
    if (isNaN(enteredPrice) || enteredPrice <= 0) {
      return 0;
    }
    return client.quantity * enteredPrice;
  };

  const getGrandTotal = () => {
    return clients.reduce((total, client) => total + getClientTotal(client), 0);
  };

  const submitAllOrders = async () => {
    setIsLoading(true);
    let hasError = false;

    const validClients = clients.filter(client => {
      const hasValidInfo = client.nom && client.phone && client.city && client.address;
      const hasValidQuantity = client.quantity > 0;
      const sellingPriceFloat = parseFloat(client.sellingPrice || 0);
      const minSellingPrice = parseFloat(getProductOriginalPriceDisplay(product).toFixed(2));
      const hasValidSellingPrice = sellingPriceFloat > minSellingPrice;
      
      if (!hasValidInfo) {
        toast.error(`Veuillez remplir toutes les informations du client (Nom, Téléphone, Ville, Adresse).`);
        hasError = true;
        setIsLoading(false);
        return false;
      }

      if (!hasValidQuantity) {
        toast.error(`Le client doit commander au moins une quantité.`);
        hasError = true;
        setIsLoading(false);
        return false;
      }

      if (!hasValidSellingPrice) {
        toast.error(`Le client doit avoir un prix de vente valide (> ${minSellingPrice}).`);
        hasError = true;
        setIsLoading(false);
        return false;
      }

      if (product?.variants?.length > 0) {
        // Check variants for each section
        const invalidSections = client.variantSections.filter((section, index) => {
          const requiredVariantTypes = [...new Set(
            product.variants.flatMap(group => 
              group.details.map(detail => detail.variant.type)
            )
          )];

          const selectedVariants = section.selectedVariants || {};
          const selectedVariantTypes = Object.keys(selectedVariants);

          const missingTypes = requiredVariantTypes.filter(
            type => !selectedVariantTypes.includes(type)
          );

          if (missingTypes.length > 0) {
            const missingTypesText = missingTypes.map(t => `"${t}"`).join(', ');
            toast.error(
              `Article #${index + 1}: Veuillez sélectionner toutes les variantes requises. Manquant: ${missingTypesText}`,
              { duration: 5000 }
            );
            setIsLoading(false);
            return true;
          }
          return false;
        });

        if (invalidSections.length > 0) {
          hasError = true;
          setIsLoading(false);
          return false;
        }
      }

      return true;
    });

    if (hasError || validClients.length === 0) {
      setIsLoading(false);
      return;
    }

    const payload = validClients.map(client => ({
      name: client.nom,
      saller_id: saller_id.id,
      productId: product.id,
      phone: client.phone,
      address: client.address,
      city: client.city,
      comment: client.comment,
      delivery_price: client.delivery_price,
      change_price: client.change_price,
      quantity: client.quantity,
      selling_price_per_unit: parseFloat(client.sellingPrice),
      original_saller_price: parseFloat(getProductOriginalPriceDisplay(product).toFixed(2) || 0),
      change: client.change,
      unlockable: client.unlockable,
      confirme: client.confirme,
      variant_sections: client.variantSections.map(section => ({
        selectedVariants: section.selectedVariants,
      })),
    }));    
      
    try {
      const response = await axios.post(BASE_URL + '/api/store-order', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.code === 200) {
        toast.success("Commandes enregistrées avec succès !");
        setClients([{
          id: "1",
          nom: "",
          phone: "",
          address: "",
          city: "",
          delivery_price: 0,
          change_price: 0,
          sellingPrice: "",
          comment: "",
          quantity: 1,
          change: false,
          unlockable: true,
          confirme: false,
          variantSections: [{
            id: `variant-${Date.now()}`,
            selectedVariants: {},
          }],
        }]);
        onClose();
      } else {
        toast.error(`Échec de l'enregistrement des commandes : ${response.data.message || 'Erreur inconnue.'}`);
      }
    } catch (error) {
      console.error('Error storing order:', error);
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 422) {
          toast.error("La validation a échoué. Vérifiez vos entrées.");
          console.error("Validation Errors:", error.response.data.errors);
        } else {
          toast.error(`Erreur lors de l'enregistrement : ${error.response.data.message || error.message}`);
        }
      } else {
        toast.error(`Erreur : ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[99] animate-fade-in">
      <div
        ref={modalRef}
        className="bg-white relative rounded-xl max-md:w-[100%] w-[70%] h-[90vh] shadow-xl py-8 max-md:pl-5 max-md:pr-0 pl-8 pr-2 mx-auto animate-scale-up"
      >
        <div>
          <div className="flex justify-between w-full items-center pb-3 border-b border-gray-200 mb-4">
            <div className="flex max-md:block max-md:pt-5 items-center w-full justify-between">
              <div className="mb-4">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <ShoppingCart size={35} className="text-[var(--color-primary-500)]" />
                  Système de commande
                </h1>
                <p className="text-gray-600 mt-2 text-sm">Ajouter plusieurs clients et leurs commandes</p>
              </div>
              <div className='pr-10 max-md:pr-6 mt-2'>
                <div className="absolute top-3 right-3 ">
                  <button
                    onClick={onClose}
                    className="text-sm cursor-pointer rounded-lg text-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    <X />
                  </button>
                </div>
                <div className="flex max-md:justify-between items-center gap-4">
                  <div className="text-right max-md:text-left">
                    <div className="text-sm text-gray-600">Grand Total</div>
                    <div className="text-lg font-bold text-[var(--color-primary-500)]">{getGrandTotal().toFixed(2)} MAD</div>
                  </div>
                  <button
                    onClick={submitAllOrders}
                    disabled={isLoading}
                    className={`px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all duration-300 ${
                      isLoading
                        ? "bg-gray-400 cursor-not-allowed scale-95 opacity-80"
                        : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] cursor-pointer scale-100"
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                        Enregistrement...
                      </span>
                    ) : (
                      "Enregistrer tout"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='w-full h-[85.5%] max-md:h-[70%] pr-6 overflow-x-auto'>
          <div className="mb-4 ">
            <div className="space-y-8">
              {clients.map((client, clientIndex) => (
                <div key={client.id} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-300)] p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white">Client #{clientIndex + 1}</h3>
                      {clients.length > 1 && (
                        <button onClick={() => removeClient(client.id)} className="text-white cursor-pointer hover:text-red-700">
                          <Trash2 size={20} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="px-4 pt-3 pb-1">
                    <div className="grid grid-cols-12 gap-4 items-center border-b border-gray-100 last:border-b-0 mb-4">
                      <div className="col-span-2">
                        <img
                          src={`${BASE_URL}/storage/${product.images?.[0]?.image || product.image_py}`}
                          alt={product.name}
                          width={200}
                          height={100}
                          className="w-16 h-16 object-cover rounded"
                        />
                      </div>
                      <div className="col-span-3">
                        <h4 className="font-medium text-gray-900">{product.name}</h4>
                        <h4 className="text-xs text-gray-900">{product.category?.title}</h4>
                      </div>
                      <div className="col-span-2 max-md:col-span-4">
                        {product.status === "active" ? (
                          <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                            In Stock
                          </span>
                        ) : (
                          <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                            Out of Stock
                          </span>
                        )}
                      </div>
                      <div className="col-span-5 flex items-center">
                        <span className="font-semibold text-[var(--color-primary-500)]">{getProductOriginalPriceDisplay(product).toFixed(2)} MAD</span>
                        {product.discount && product.discount.status === "true" && new Date() >= new Date(product.discount.date_start) && new Date() <= new Date(product.discount.date_end) && (
                          <div className='flex items-center gap-1'>
                            <span className="ml-2 text-sm text-gray-500 line-through">{parseFloat(product.price.saller_price).toFixed(2)} MAD</span>
                            <span className="rounded-lg bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                              -{product.discount.value || 0}% OFF
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {product.variants && product.variants.length > 0 && (
                    <div className="space-y-2">
                      {client.variantSections.map((section, sectionIndex) => (
                        <div key={section.id} className="border pb-3 border-gray-200 rounded-lg px-4 pt-1">
                          <h4 className="font-medium">Article #{sectionIndex + 1}</h4>
                          <VariantSelector
                            type={true}
                            variants={product.variants}
                            onVariantChange={(variants) => updateVariantSelection(client.id, sectionIndex, variants)}
                            initialSelectedVariants={section.selectedVariants || {}}
                          />
                        </div>
                      ))}
                    </div>
                    )}

                    <div className="grid grid-cols-2 max-xl:grid-cols-1 items-center justify-start gap-5 mt-5 flex-wrap">
                      <div className='grid grid-cols-2 gap-x-5'>
                        <div>
                          <div className="mb-2 flex-grow">
                            <label htmlFor={`client-${client.id}-nom`} className="block font-semibold text-gray-900 mb-1 text-xs">
                              Nom <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id={`client-${client.id}-nom`}
                              name="nom"
                              value={client.nom}
                              onChange={(e) => updateClientInfo(client.id, 'nom', e.target.value)}
                              placeholder="Entrez le nom..."
                              className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                              required
                            />
                          </div>
                          <div className="mb-2 flex-grow">
                            <label htmlFor={`client-${client.id}-phone`} className="block font-semibold text-gray-900 mb-1 text-xs">
                              Téléphone <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="tel"
                              id={`client-${client.id}-phone`}
                              name="phone"
                              value={client.phone}
                              onChange={(e) => updateClientInfo(client.id, 'phone', e.target.value)}
                              placeholder="Entrez le téléphone..."
                              className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <div className="mb-2 flex-grow cursor-pointer">
                            <label htmlFor={`client-${client.id}-city`} className="block font-semibold text-gray-900 mb-1 text-xs">
                              Ville <span className="text-red-500">*</span>
                            </label>
                            <Select
                              inputId={`client-${client.id}-city`}
                              options={cityOptions}
                              value={cityOptions.find(option => option.label === client.city)}
                              onChange={(selectedOption) => {
                                const deliverPrice = selectedOption?.deliver ?? 0;
                                const changePrice = selectedOption?.change ?? 0;

                                updateClientInfo(client.id, null, {
                                  city: selectedOption.label,
                                  delivery_price: deliverPrice,
                                  change_price: changePrice,
                                });
                              }}
                              placeholder="Choisissez une ville..."
                              className={`w-full py-0.5 px-1 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  backgroundColor: 'transparent',
                                  boxShadow: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                }),
                              }}
                              isSearchable
                            />
                          </div>
                          {/* <div className="grid grid-cols-2 gap-4"> */}
                          <div className="mb-2 flex-grow">
                            <label htmlFor={`client-${client.id}-sellingPrice`} className="block font-semibold text-gray-900 mb-1 text-xs">
                              Prix de vente <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              id={`client-${client.id}-sellingPrice`}
                              name="sellingPrice"
                              value={client.sellingPrice}
                              onChange={(e) => updateClientInfo(client.id, 'sellingPrice', e.target.value)}
                              placeholder={product.price?.selling_price || "Entrez le prix de vente..."}
                              className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                              min="0"
                              step="0.01"
                              required
                            />
                          </div>
                            
                          {/* </div> */}
                        </div>
                      </div>
                      <div className='grid grid-cols-2 max-md:grid-cols-1 gap-x-5'>
                        <div>
                          <div className="mb-2 flex-grow">
                            <label htmlFor={`client-${client.id}-address`} className="block font-semibold text-gray-900 mb-1 text-xs">
                              Adresse <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id={`client-${client.id}-address`}
                              name="address"
                              value={client.address}
                              onChange={(e) => updateClientInfo(client.id, 'address', e.target.value)}
                              placeholder="Entrez l'adresse..."
                              className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                              required
                            />
                          </div>
                          <div className="mb-2 flex-grow">
                            <label htmlFor={`client-${client.id}-quantity`} className="block font-semibold text-gray-900 mb-1 text-xs">
                              Quantité <span className="text-red-500">*</span>
                            </label>
                            <div className="w-full flex items-center justify-between text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]">
                              <button
                                onClick={() => updateQuantity(client.id, client.quantity - 1)}
                                className="py-3 px-4 cursor-pointer hover:bg-gray-100 transition-colors duration-200 rounded-l-lg"
                                disabled={client.quantity <= 1}
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <span className="px-4 py-3 text-center border-l border-r border-gray-300">
                                {client.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(client.id, client.quantity + 1)}
                                className="py-3 px-4 cursor-pointer hover:bg-gray-100 transition-colors duration-200 rounded-r-lg"
                                disabled={client.quantity >= (product.price?.stock || 0)}
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          
                        </div>
                        <div className='flex items-start justify-start'>
                          <div className="w-full">
                            <label htmlFor={`client-${client.id}-comment`} className="block font-semibold text-gray-900 mb-1 text-xs">
                              Commentaire
                            </label>
                            <textarea
                              id={`client-${client.id}-comment`}
                              name="comment"
                              value={client.comment}
                              onChange={(e) => updateClientInfo(client.id, 'comment', e.target.value)}
                              placeholder="Entrez le commentaire..."
                              className={`w-full resize-none py-3 px-4 h-28 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                  

                    <div className="pt-1.5 border-t border-gray-200 mt-4">
                      <div className="flex justify-between max-md:block">
                        <div className="flex items-center gap-4 ">
                            <div className="flex items-center hidden">
                              <input
                                type="checkbox"
                                id={`client-${client.id}-change`}
                                name="change"
                                checked={client.change || false}
                                onChange={(e) => updateOrderOption(client.id, 'change', e.target.checked)}
                                className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                              />
                              <label htmlFor={`client-${client.id}-change`} className="ml-3 text-xs text-base text-gray-700 cursor-pointer">
                                Change
                              </label>
                            </div>

                            <div className="flex items-center hidden">
                              <input
                                type="checkbox"
                                id={`client-${client.id}-unlockable`}
                                name="unlockable"
                                checked={client.unlockable || false}
                                onChange={(e) => updateOrderOption(client.id, 'unlockable', e.target.checked)}
                                className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                              />
                              <label htmlFor={`client-${client.id}-unlockable`} className="ml-3 text-xs text-base text-gray-700 cursor-pointer">
                                L'ouverture de colis
                              </label>
                            </div>
                            <div className="flex items-center hidden">
                              <input
                                type="checkbox"
                                id={`client-${client.id}-confirme`}
                                name="confirme"
                                checked={client.confirme || false}
                                onChange={(e) => updateOrderOption(client.id, 'confirme', e.target.checked)}
                                className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)] h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                              />
                              <label htmlFor={`client-${client.id}-confirme`} className="ml-3 text-xs text-base text-gray-700 cursor-pointer">
                                Déjà confirmé
                              </label>
                            </div>
                          </div>
                        <div className='flex items-center gap-8 max-md:mb-2 max-md:gap-3'></div>
                        <div className="text-right block ">
                          <span className="font-semibold text-gray-900 block my-[-5px]">
                            Total Client: <span className="text-[var(--color-primary-500)] font-bold">{getClientTotal(client).toFixed(2)} MAD</span>
                          </span>
                          <span className="text-sm">
                            <span className="text-green-600 font-semibold">
                              Profit :
                              {" "+getClientProfit(client).toFixed(2) || 0} MAD
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
            <button
              onClick={addClient}
              className="flex items-center cursor-pointer border border-[var(--color-primary-500)] gap-2 p-10 text-[var(--color-primary-500)] rounded-full hover:bg-[var(--color-primary-400)] hover:text-white transition-colors font-medium"
            >
              <Plus size={40} />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}